import Constants from '@/constants/const';
import { getCookie } from '@/utils/cookie';
import qs from 'qs';

export const isServer: boolean = typeof window === 'undefined';

export const buildUrlWithOrigin = (path: string) => {
  const host = process.env.WEB_HOST || process.env.NEXT_PUBLIC_WEB_HOST;
  if (path.indexOf(host) === -1) {
    if (path[0] === '/') {
      return `${host}${path || ''}`;
    } else {
      return `${host}/${path || ''}`;
    }
  } else {
    return path;
  }
};

export function getPathNoLangOnClient(url?: string) {
  if (!window) return '';
  const pathname = url || window.location.pathname;
  const pathNoLang = pathname.replace(Constants.LANGUAGE_REGEXP_TWO, '');
  if (pathNoLang.indexOf('/') !== 0) {
    return '/' + pathNoLang;
  }
  return pathNoLang;
}

/**
 * 纠正传入的URL的语言位置，并返回纠正后的URL
 * */
export const correctUrlLanguage = (url: string) => {
  const hostReg = /\/\/([^/]+)\//;
  let urlHost = hostReg.exec(url)?.[1] || '';
  let localHost = window.location.host;
  if (urlHost !== localHost) return url;
  let localLang = getCookie(Constants.LANGUAGE);
  localLang = localLang === Constants['LANGUAGE_CHINESE_T_ALIAS'] ? Constants['LANGUAGE_CHINESE_T'] : localLang;
  let urlLang = new RegExp(`${localHost}/([^/]+)`).exec(url)?.[1] || '';
  if (!Constants.LANGUAGES.includes(urlLang)) {
    urlLang = '';
  }
  if (urlLang === localLang) return url;
  return url.replace(`${localHost}${urlLang === '' ? '' : '/'}${urlLang}`, `${localHost}/${localLang}`);
};

export const getOrigin = (ctx?) => {
  if (isServer) {
    const req = ctx?.req;
    return `${req?.protocol}://${req?.hostname}`;
  }
  return window.location.origin;
};

export function convertObjectToUrlParams<T extends { [key: string]: string | number }>(obj: T): string {
  if (!obj || Object.keys(obj).length < 1) {
    return '';
  }

  const searchStr = Object.keys(obj)
    .map(item => (obj[item] ? `${item}=${obj[item]}` : ''))
    .filter(param => param !== '')
    .join('&');

  if (!searchStr) return '';

  return `?${searchStr}`;
}

export type BuildProductDetailUrlParams = {
  lang: string;
  productPathSegment: string;
  linkParams?: Record<string, any>;
  refPageCtxParam?: { refPageCtx?: string };
  extraParams?: Record<string, string | number>;
};

/**
 * 构建商品详情页完整 URL（含 origin），统一处理 linkParams、refPageCtx 等参数。
 * 当 refPageCtxParam 存在时，若未出现在 query 中会兜底追加，确保 Weee App 下 mod_nm 追踪正确。
 */
export function buildProductDetailUrl(options: BuildProductDetailUrlParams): string {
  const { lang, productPathSegment, linkParams = {}, refPageCtxParam = {}, extraParams = {} } = options;
  const path = `/${lang}/product/${productPathSegment}`;
  const merged = {
    ...linkParams,
    ...extraParams,
    ...refPageCtxParam
  } as Record<string, string | number>;
  const paramsStr = convertObjectToUrlParams(merged);
  let url = path + paramsStr;
  if (refPageCtxParam?.refPageCtx && !url.includes('refPageCtx=')) {
    url += (url.includes('?') ? '&' : '?') + `refPageCtx=${refPageCtxParam.refPageCtx}`;
  }
  return buildUrlWithOrigin(url);
}

/**
 * store 参数需要在后面访问时去掉
 * @param paramsArray 需要从url上剔除哪些参数然后刷新页面
 */
export const reloadPageAndRemoveParams = (paramsArray?: string[], addParams?: ObjectType) => {
  let params = qs.parse(window.location.search.slice(1));
  if (Array.isArray(paramsArray)) {
    paramsArray.forEach(str => {
      if (params[str]) {
        delete params[str];
      }
    });
  }
  if (params['grocery-store']) {
    delete params['grocery-store']; //这个一定要删除的
  }
  if (params['set_lang']) {
    delete params['set_lang']; //这个一定要删除的
  }
  params = {
    ...params,
    ...addParams
  };
  const url = `${window.location.origin}${window.location.pathname}${qs.stringify(params, {
    addQueryPrefix: true
  })}`;
  window.location.href = url;
};

export const toString = newQuery => {
  return qs.stringify(newQuery, { addQueryPrefix: true, encodeValuesOnly: true });
};

/**
 * Add param before others 在地址上添加query
 * @param url
 * @param parameterName
 * @param parameterValue
 * @param atStart
 *
 * @returns
 */
export function addParameterToUrl(url, parameterName, parameterValue, atStart?) {
  const replaceDuplicates = true;
  let urlhash, cl;
  if (url.indexOf('#') > 0) {
    cl = url.indexOf('#');
    urlhash = url.substring(url.indexOf('#'), url.length);
  } else {
    urlhash = '';
    cl = url.length;
  }
  const sourceUrl = url.substring(0, cl);

  const urlParts = sourceUrl.split('?');
  let newQueryString = '';

  if (urlParts.length > 1) {
    const parameters = urlParts[1].split('&');
    for (let i = 0; i < parameters.length; i++) {
      const parameterParts = parameters[i].split('=');
      if (!(replaceDuplicates && parameterParts[0] == parameterName)) {
        if (newQueryString === '') newQueryString = '?';
        else newQueryString += '&';
        newQueryString += parameterParts[0] + '=' + (parameterParts[1] ? parameterParts[1] : '');
      }
    }
  }
  if (newQueryString === '') newQueryString = '?';

  if (atStart) {
    newQueryString =
      '?' + parameterName + '=' + parameterValue + (newQueryString.length > 1 ? '&' + newQueryString.substring(1) : '');
  } else {
    if (newQueryString !== '' && newQueryString != '?') newQueryString += '&';
    newQueryString += parameterName + '=' + (parameterValue ? parameterValue : '');
  }
  return urlParts[0] + newQueryString + urlhash;
}

/**
 * 获取当前url的不带语言位的pathname
 * @param url
 * @returns
 */
export function getPathName(url: string) {
  if (!url) return '';
  const host = process.env.WEB_HOST || process.env.NEXT_PUBLIC_WEB_HOST;
  let pathname = '';
  if (url.indexOf(host) === -1) {
    pathname = url;
  } else {
    pathname = url.replace(host, '');
  }
  const pathNoLang = pathname.replace(Constants.LANGUAGE_REGEXP_TWO, '');
  if (pathNoLang.indexOf('/') !== 0) {
    return '/' + pathNoLang;
  }
  return pathNoLang;
}

/**
 * 移除当前url上指定的参数，支持移除多个参数
 * 返回删除参数后url
 * @param params
 * @returns
 */
export function removeUrlParameters(originUrl, params) {
  const url = new URL(originUrl, window.location.href);
  params.forEach(param => url.searchParams.delete(param));
  return url.toString();
}

/**
 * 获取当前url参数，转成对象
 * @param search next useSearchParams对象
 * @returns
 */
export const getCurrentParams = (): Record<string, any> => {
  if (typeof window !== 'object') {
    return {};
  }
  const urlSearch = new URLSearchParams(window.location.search);
  const result: Record<string, any> = {};

  urlSearch.forEach((val, key) => {
    if (val !== 'undefined' && val !== 'null' && val !== '') {
      result[key] = val;
    }
  });
  return result;
};

export const removeEmptyValue = (data: ObjectType) => {
  if (typeof data === 'object' && Object.keys(data).length) {
    const result: ObjectType = {};
    Object.keys(data).forEach(key => {
      const value = data[key];
      if (value !== '' && value !== 'null' && value !== 'undefined' && value !== '{}') {
        result[key] = data[key];
      }
    });

    return result;
  }
  return {};
};

export const syncUrlParams = (path: string, params: ObjectType) => {
  const urlObj = new URL(path);
  const query = { ...params };
  Object.keys(query).forEach(item => {
    if (query[item] !== null && query[item] !== undefined) {
      urlObj.searchParams.set(item, `${query[item]}`);
    }
  });
  window.history.replaceState({ options: { __no_replace: 1 } }, '', urlObj.toString());
};

/**
 * 如果url上有同名query， qs.parse 格式化后会返回一个数组
 * 如果是数组暂时只处理为取数组第一项
 * @param value 某一query的值
 * @returns
 */
export const getSafeQueryValue = value => {
  if (!value) {
    return '';
  }
  if (Array.isArray(value)) {
    return value[0] ? String(value[0]) : '';
  }
  return String(value);
};

export const getLinkWithLang = (url: string) => {
  const lang = getCookie(Constants.COOKIE_LANGUAGE) ?? 'en';
  const langLink = url.replace('{lang}', lang);
  return langLink;
};

/**
 * 获取url中指定参数的最后一个值
 * @param url
 * @param param
 * @returns
 */
export const getLastURLParam = (url: string, param: string) => {
  if (!url || !param) return '';
  const urlObj = new URL(url);
  const paramsArray = urlObj.searchParams.getAll(param);
  return paramsArray[paramsArray.length - 1] ?? '';
};

export const searchParamsToObject = (searchParams: any) => {
  const params = {} as Record<string, any>;
  for (const [key, value] of searchParams) {
    params[key] = value;
  }
  return params || {};
};

// 处理URL参数中的utm_和itm_开头的参数
export const extractUtmItmParams = (url: string) => {
  // 添加URL有效性检查
  if (!url || typeof url !== 'string') {
    return {};
  }

  try {
    // 如果是相对路径，使用当前域名作为基础URL
    const urlObj = new URL(url, typeof window !== 'undefined' ? window.location.href : undefined);
    const { searchParams } = urlObj;

    return Array.from(searchParams.entries()).reduce((acc, [key, value]) => {
      const cleanKey = key.replace(/\[\d*\]$/, '');
      if (cleanKey.startsWith('utm_') || cleanKey.startsWith('itm_')) {
        acc[cleanKey] = value;
      }
      return acc;
    }, {} as Record<string, string>);
  } catch (error) {
    console.warn('Failed to parse URL in extractUtmItmParams:', url, error);
    return {};
  }
};

export const getPdpAdsParams = (data: Record<string, any>): Record<string, unknown> => {
  const { ads_creative } = data ?? {};
  if (!ads_creative) return {};
  const paramsString = ads_creative?.trackRedirectUrl?.split('?')?.[1];
  const paramsObj = new URLSearchParams(`?${paramsString}`);
  return Object.fromEntries(paramsObj.entries());
};

// 抹平 URL 中的查询参数、语言前缀和末尾斜线差异
// 将 /[lang]/search?xxxx 格式的url转成纯路由格式：/search
export const getPureUrl = (langs: string[], urlStr: string) => {
  let path = urlStr.split('?')[0] || '/'; // 去掉查询参数
  // 移除 Next.js 自动添加的语言前缀 (如 /zh/search -> /search)
  langs.forEach(locale => {
    if (path.startsWith(`/${locale}/`)) {
      path = path.replace(`/${locale}/`, '/');
    } else if (path === `/${locale}`) {
      path = '/';
    }
  });
  return path.replace(/\/$/, '') || '/'; // 移除末尾斜线并始终以 / 开头
};
